
import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import GasStatCards from './GasStatCards';
import GasConsumptionBarChart from './GasConsumptionBarChart';
import GasDistributionPieChart from './GasDistributionPieChart';
import GasFlowChart from './GasFlowChart';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BrainCircuit, Flame, AlertCircle, ArrowDownCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const GasAIAdvisoryTab = () => {
  const [activeSubTab, setActiveSubTab] = useState("advisories");

  const gasAdvisories = [
    {
      id: 1,
      title: "Gas Pressure Optimization",
      description: "System has detected inconsistent pressure levels in sector B-12. Consider recalibrating pressure regulators to improve efficiency.",
      impact: "Medium",
      category: "Efficiency"
    },
    {
      id: 2,
      title: "Leak Risk Detection",
      description: "Unusual flow patterns detected in the northwest pipeline. Recommend inspection of joints and connectors within the next 48 hours.",
      impact: "High",
      category: "Safety"
    },
    {
      id: 3,
      title: "Consumption Anomaly",
      description: "Building 7 gas usage has increased by 22% compared to similar weather conditions last year. Possible equipment malfunction or inefficiency.",
      impact: "Medium",
      category: "Usage"
    },
    {
      id: 4,
      title: "Preventive Maintenance",
      description: "Gas meter GM-234 showing early signs of measurement drift. Schedule calibration within next maintenance window.",
      impact: "Low",
      category: "Maintenance"
    }
  ];

  return (
    <div className="h-full">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="h-full flex flex-col">
        <div className="flex-none">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="advisories" className="flex items-center gap-1 text-xs py-1">
              <BrainCircuit className="h-3 w-3" />
              <span className="hidden sm:inline">Advisories</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-1 text-xs py-1">
              <Flame className="h-3 w-3" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="distribution" className="flex items-center gap-1 text-xs py-1">
              <ArrowDownCircle className="h-3 w-3" />
              <span className="hidden sm:inline">Distribution</span>
            </TabsTrigger>
            <TabsTrigger value="flow" className="flex items-center gap-1 text-xs py-1">
              <Flame className="h-3 w-3" />
              <span className="hidden sm:inline">Flow</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-hidden mt-2">
          <TabsContent value="advisories" className="h-full overflow-auto m-0">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <BrainCircuit className="h-5 w-5 text-amber-500" />
                  Gas System AI Advisories
                </h2>
                <Button variant="outline" size="sm">Export Insights</Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {gasAdvisories.map(advisory => (
                  <Card key={advisory.id} className="border-l-4 border-l-amber-500">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg font-medium flex items-center gap-2">
                          {advisory.category === "Efficiency" && <Flame className="h-4 w-4 text-amber-500" />}
                          {advisory.category === "Safety" && <AlertCircle className="h-4 w-4 text-red-500" />}
                          {advisory.category === "Usage" && <ArrowDownCircle className="h-4 w-4 text-blue-500" />}
                          {advisory.category === "Maintenance" && <AlertCircle className="h-4 w-4 text-orange-500" />}
                          {advisory.title}
                        </CardTitle>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          advisory.impact === "High" ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" : 
                          advisory.impact === "Medium" ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" : 
                          "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                        }`}>
                          {advisory.impact} Impact
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 dark:text-gray-300">{advisory.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="h-full overflow-auto m-0">
            <GasStatCards />
          </TabsContent>

          <TabsContent value="distribution" className="h-full overflow-auto m-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <GasConsumptionBarChart />
              <GasDistributionPieChart />
            </div>
          </TabsContent>
          
          <TabsContent value="flow" className="h-full overflow-auto m-0">
            <GasFlowChart />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default GasAIAdvisoryTab;
